import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
import { delay } from "./Utils";

gsap.registerPlugin(Draggable);

class DragFreeform {
  constructor(el, callback, maxCount = 5) {
    this.el = el;
    this.callback = callback;
    this.dragMaxCount = maxCount;

    // --- Elements ---
    this.container = document.querySelector(this.el);
    this.draggables = this.container.querySelectorAll(".js-draggable");
    this.dropZones = this.container.querySelectorAll(".dropzone");
    this.miniMapSlots = this.container.querySelectorAll(".js-draggables-mini [data-slot]");

    // --- State ---
    this.zoneOccupancy = new Map();
    this.productState = new Map();

    // --- Configuration ---
    this.dragFreeOverlapThreshold = "25%";
    this.dragFreeformCurrentZone = null;
    this.MINIMAP_ACTIVE_CLASS = "!bg-se_green";

    this.init();
  }

  /**
   * Calculates the distance needed to move from current position to the dropzone
   */
  _getRelativeSnapOffsets(draggable, dropZoneEl) {
    const dragElPos = draggable.target.getBoundingClientRect();
    const dropElPos = dropZoneEl.getBoundingClientRect();

    return {
      offsetX: dropElPos.left - dragElPos.left,
      offsetY: dropElPos.top - dragElPos.top,
    };
  }

  _updateMinimap(slot, product, isOccupied) {
    const index = slot - 1;
    const slotEl = this.miniMapSlots[index];

    if (!slotEl) return;

    if (isOccupied) {
      slotEl.classList.add(this.MINIMAP_ACTIVE_CLASS);
      slotEl.textContent = product.slice(0, -2);
    } else {
      slotEl.classList.remove(this.MINIMAP_ACTIVE_CLASS);
      slotEl.textContent = slotEl.dataset.default;
    }
  }

  _handleDrop(draggable) {
    const draggedEl = draggable.target;
    const product = draggedEl.dataset.product;
    const currentZone = this.dragFreeformCurrentZone;
    const currentState = this.productState.get(product);

    const oldSlot = currentState.slot;
    const newSlot = currentZone ? parseInt(currentZone.dataset.slot) : null;

    // --- 1. Revert/Reset Logic ---
    const isDroppedOutside = currentZone === null;
    const isNewSlotOccupied = newSlot && this.zoneOccupancy.has(newSlot) && newSlot !== oldSlot;

    if (isDroppedOutside || isNewSlotOccupied) {
      // If it was in a slot and is now dropped outside, free the old slot
      if (oldSlot) {
        this.zoneOccupancy.delete(oldSlot);
        this._updateMinimap(oldSlot, null, false);
        // Reset state to origin (0,0)
        this.productState.set(product, { slot: null, snappedX: 0, snappedY: 0 });
      }

      // Animate back to the last valid position (0,0 if it's returning home)
      gsap.to(draggedEl, { 
        duration: 0.3, 
        x: this.productState.get(product).snappedX, 
        y: this.productState.get(product).snappedY,
        ease: "power2.out"
      });
      return;
    }

    // --- 2. Change Slots Logic ---
    if (oldSlot && oldSlot !== newSlot) {
      this.zoneOccupancy.delete(oldSlot);
      this._updateMinimap(oldSlot, null, false);
    }

    // --- 3. Snap to New Slot ---
    if (newSlot) {
      const { offsetX, offsetY } = this._getRelativeSnapOffsets(draggable, currentZone);

      // Calculate new target by adding offset to current GSAP transform values
      const targetX = gsap.getProperty(draggedEl, "x") + offsetX;
      const targetY = gsap.getProperty(draggedEl, "y") + offsetY;

      // Update State
      this.zoneOccupancy.set(newSlot, product);
      this.productState.set(product, {
        slot: newSlot,
        snappedX: targetX,
        snappedY: targetY,
      });

      this._updateMinimap(newSlot, product, true);

      gsap.to(draggedEl, {
        duration: 0.3,
        x: targetX,
        y: targetY,
        ease: "back.out(1.2)"
      });
    }

    // --- 4. Completion ---
    if (this.zoneOccupancy.size === this.dragMaxCount) {
      this._saveAndCallback();
    }
  }

  _saveAndCallback() {
    const outputArray = Array.from(this.zoneOccupancy.entries())
      .sort(([slotA], [slotB]) => slotA - slotB)
      .map(([slot, product]) => [
        String(slot),
        product.replace(/\s/g, "_").replace(/_\d/g, ""),
      ]);

    localStorage.setItem("wastewaterpositions", JSON.stringify(outputArray));
    this.callback();
  }

  init() {
    const self = this; // Cleaner than using this.that

    this.draggables.forEach((el) => {
      const product = el.dataset.product;

      this.productState.set(product, {
        slot: null,
        snappedX: 0,
        snappedY: 0,
      });

      Draggable.create(el, {
        type: "x,y",
        edgeResistance: 0.65,
        onPress: function () {
          gsap.set(this.target, { zIndex: 100 });
        },
        onDrag: function () {
          self.dragFreeformCurrentZone = null;
          for (const zone of self.dropZones) {
            if (this.hitTest(zone, self.dragFreeOverlapThreshold)) {
              self.dragFreeformCurrentZone = zone;
              // Visual feedback for hover could go here
              break;
            }
          }
        },
        onDragEnd: function () {
          gsap.set(this.target, { zIndex: "auto" });
          self._handleDrop(this);
        },
      });
    });
  }

  reset() {
    delay(500).then(() => {
      this.zoneOccupancy.clear();
      this.productState.forEach((state, product) => {
        state.slot = null;
        state.snappedX = 0;
        state.snappedY = 0;

        const el = this.container.querySelector(`[data-product="${product}"]`);
        gsap.to(el, { duration: 0.5, x: 0, y: 0, opacity: 1, ease: "power2.inOut" });

        const draggableInstance = Draggable.get(el);
        if (draggableInstance) {
          draggableInstance.update(); // Sync draggable with new GSAP position
        }
      });

      this.miniMapSlots.forEach((el) => {
        el.classList.remove(this.MINIMAP_ACTIVE_CLASS);
        el.textContent = el.dataset.default;
      });
    });
  }
}

export { DragFreeform };